import { HStack, SimpleGrid, Spinner, Stack, Text, Textarea } from '@chakra-ui/react';
import { join } from '@pelicargo/utils';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { CONTACT_TAGS } from '../../utils/constants';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleField } from '../SimpleField';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleTextarea } from '../SimpleTextarea';

/**
 * Free-text auto-fill: paste a raw contact block and, on blur, parse it into the
 * structured Contact fields below (reusing the `parseStructuredText` procedure).
 * Only non-empty parsed fields are written, so it never clobbers manual edits.
 */
const ContactAutofill = () => {
  const { setValue, getValues } = useFormContext();
  const { mutateAsync: parseStructuredText, isLoading } = trpc.parseStructuredText.useMutation();
  const [text, setText] = useState('');

  const handleBlur = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const { data } = await parseStructuredText({ text: trimmed, type: 'Contact' });
      const fill = (field: string, value: unknown) => {
        if (typeof value === 'string' && value.trim()) {
          setValue(field, value, { shouldDirty: true, shouldValidate: true });
        }
      };
      fill('full_name', data.full_name);
      fill('title', data.title);
      fill('email', data.email);
      fill('phone_number', data.phone_number);
      const address = (data.address ?? {}) as Record<string, unknown>;
      fill('address.line_one', address.line_one);
      fill('address.line_two', address.line_two);
      fill('address.city', address.city);
      fill('address.state', address.state);
      fill('address.zip_code', address.zip_code);
      fill('address.country', address.country);
      fill('notes', data.notes);
      // Preferred name is required but not a parsed field — seed it from the name.
      if (typeof data.full_name === 'string' && data.full_name.trim() && !getValues('preferred_name')) {
        setValue('preferred_name', data.full_name, { shouldDirty: true, shouldValidate: true });
      }
    } catch {
      // Leave the fields untouched on failure; the operator can fill them manually.
    }
  };

  return (
    <SimpleField
      name="__contact_autofill"
      label="Auto-fill from text"
      helper="Paste details and click away to fill the fields below."
    >
      <Stack position="relative">
        <Textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={handleBlur}
          placeholder="Paste a name, company, address, phone, email…"
          rows={4}
          variant="filled"
          isDisabled={isLoading}
        />
        {isLoading ? <Spinner size="xs" color="gray.400" position="absolute" top="2" right="2" /> : null}
      </Stack>
    </SimpleField>
  );
};

export const ContactForm = () => {
  const { knownTenants } = useAuth();
  const { data: accounts } = trpc.getAllAccounts.useQuery();

  const accountOptions = useMemo(() => {
    return (
      accounts?.map((account) => ({
        value: String(account?.id),
        label: `${account?.name} (${String(account?.id)})`,
      })) ?? []
    );
  }, [accounts]);

  const getAssociationTitle = useCallback(
    (association: any) => {
      const providers = association?.providers?.length ? join(association?.providers) : '-';

      const airports = association?.airports?.length ? join(association?.airports) : '-';
      const accounts = association?.accounts?.length
        ? join(
            association?.accounts?.map(
              (accountId) =>
                accountOptions?.find((accountOption) => Number(accountOption.value) === Number(accountId))?.label,
            ),
          )
        : '-';
      const tags = association?.tags?.length ? join(association.tags) : '-';

      // for some reason new lines aren't respected
      return (
        <Text display="inline">
          Airlines: {providers} Airports: {airports} Accounts: {accounts} Tags: {tags}
        </Text>
      );
    },
    [accountOptions],
  );

  const tagsOptions = useMemo(() => {
    return CONTACT_TAGS.map((option) => {
      return { label: option, value: option };
    });
  }, []);

  return (
    <Stack direction="column" spacing={4} w="full">
      <ContactAutofill />
      <Stack direction={{ base: 'column', md: 'row' }} w="full">
        <SimpleInput name="full_name" label="Full Name" placeholder="Someone" isRequired />
        <SimpleInput name="preferred_name" label="Preferred Name" placeholder="Awesome" isRequired />
      </Stack>
      <SimpleInput name="title" label="Title" />
      <SimpleInput name="email" label="Email" placeholder="someone@awesomecompany.com" isRequired />

      <SimpleInput name="phone_number" label="Phone" placeholder="Call me maybe?" />

      <SimpleInput name="address.line_one" label="Address line 1" placeholder="123 Main St" />
      <SimpleInput name="address.line_two" label="Address line 2" placeholder="Suite 400" />
      <Stack direction={{ base: 'column', md: 'row' }} w="full">
        <SimpleInput name="address.city" label="City" placeholder="Los Angeles" />
        <SimpleInput name="address.state" label="State / Province" placeholder="CA" />
      </Stack>
      <Stack direction={{ base: 'column', md: 'row' }} w="full">
        <SimpleInput name="address.zip_code" label="Postal code" placeholder="90045" />
        <SimpleInput name="address.country" label="Country" placeholder="United States" />
      </Stack>

      <SimpleTextarea label="Notes" placeholder="Notes" name="notes" rows={8} />
      <HStack alignItems="end">
        <SimpleReactSelect
          name="tenants"
          label="Tenants"
          options={knownTenants.map((t) => ({
            label: t,
            value: t,
          }))}
          isMulti
          menuPlacement="top"
        />
        <SimpleCheckbox name="is_impersonation_contact" label="Is contact for external tenant use" />
      </HStack>
      <SimpleFieldArray
        name="associations"
        addLabel="Add Association"
        accordionTitle={getAssociationTitle}
        ignoreStyles
        form={({ prefix }) => {
          return (
            <SimpleGrid columns={2} spacing={2}>
              <SimpleField name="providers" label="Airlines">
                <AirlineInput name={`${prefix}.providers`} placeholder="Airlines" isMulti menuPlacement="top" />
              </SimpleField>
              <SimpleField name="airports" label="Airports">
                <AirportInput name={`${prefix}.airports`} placeholder="Airports" isMulti menuPlacement="top" />
              </SimpleField>

              <SimpleReactSelect
                name={`${prefix}.tags`}
                label="Tags"
                type="creatable"
                options={tagsOptions}
                isMulti
                menuPlacement="top"
              />
              <SimpleReactSelect
                name={`${prefix}.accounts`}
                label="Accounts"
                options={accountOptions}
                isMulti
                menuPlacement="top"
              />
            </SimpleGrid>
          );
        }}
        atLeastOneFieldOpen
      />
    </Stack>
  );
};
